import { useEffect } from "react";

/** DOM helpers hoisted out of the effect for lower complexity and testability. */
function upsertMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertJsonLd(jsonLd) {
  let script = document.head.querySelector('script[data-seo-ld="1"]');
  if (jsonLd) {
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-ld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
  } else if (script) {
    script.remove();
  }
}

function resolveCanonical(path) {
  const base = (typeof window !== "undefined" && window.location.origin) || "";
  if (path) return `${base}${path}`;
  return typeof window !== "undefined" ? window.location.href : "";
}

function applyMetaTags({ title, description, image, ogType }) {
  if (title) document.title = title;
  if (description) upsertMeta("description", description);
  upsertMeta("og:title", title, "property");
  upsertMeta("og:description", description, "property");
  upsertMeta("og:type", ogType, "property");
  if (image) upsertMeta("og:image", image, "property");
}

/**
 * <Seo> — sets document.title, meta description, canonical URL, and optional JSON-LD.
 * Client-side only (CRA doesn't SSR); sufficient for social crawlers that execute JS + for
 * canonical + description that Google reads after JS render.
 */
export default function Seo({ title, description, path, image, jsonLd, ogType = "website" }) {
  useEffect(() => {
    applyMetaTags({ title, description, image, ogType });
    upsertCanonical(resolveCanonical(path));
    upsertJsonLd(jsonLd);
  }, [title, description, path, image, ogType, jsonLd]);

  return null;
}

/** Reusable structured-data builders */
const SEED_REVIEWS = [
  { author: "Ramesh K.", rating: 5, body: "After my ACL surgery, I was worried I'd never play cricket again. The team at Physio Hyve created a rehab plan that got me back on the field stronger than before." },
  { author: "Priya S.", rating: 5, body: "I struggled with lower back pain for over two years. Within a few weeks of starting physiotherapy here, I was able to sit through my work day pain-free." },
  { author: "Anil M.", rating: 5, body: "My father had a stroke and lost movement on his right side. The neuro rehab team is patient, skilled and truly compassionate. He is now walking with a stick." },
  { author: "Sneha R.", rating: 5, body: "Frozen shoulder was making it impossible to lift my arm. The manual therapy and exercises restored my range of motion completely." },
  { author: "Vikram J.", rating: 5, body: "Post knee replacement, I was scared of moving. The step-by-step rehab plan gave me confidence and strength." },
  { author: "Meera D.", rating: 4, body: "Very clean clinic, modern equipment and thorough assessment. Dr. Amit explained my condition in detail." },
];

export const localBusinessLd = () => ({
  "@context": "https://schema.org",
  "@type": ["MedicalClinic", "LocalBusiness"],
  name: "Physio Hyve Clinic",
  image: typeof window !== "undefined" ? `${window.location.origin}/logo-icon-256.png` : "/logo-icon-256.png",
  logo: typeof window !== "undefined" ? `${window.location.origin}/logo-icon-256.png` : "/logo-icon-256.png",
  url: typeof window !== "undefined" ? window.location.origin : "",
  telephone: "+91-85120-85860",
  email: "care@physiohyve.com",
  priceRange: "₹₹",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Plot No. 115-A, Basement, Nangloi–Najafgarh Road, Ahir Mohalla",
    addressLocality: "Nangloi Jat",
    addressRegion: "Delhi",
    postalCode: "110041",
    addressCountry: "IN",
  },
  medicalSpecialty: ["Physiotherapy", "Sports Medicine", "Rehabilitation"],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00", closes: "20:00",
    },
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Sunday"], opens: "10:00", closes: "14:00" },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    reviewCount: "157",
  },
  sameAs: [
    "https://www.google.com/search?q=physiohyve",
    "https://www.justdial.com/Delhi/Physio-Hyve-Clinic-Nangloi/011PXX11-XX11-230204130018-C5E7_BZDET",
    "https://www.instagram.com/physiohyve/",
    "https://www.facebook.com/PhysioHyve/",
  ],
  review: SEED_REVIEWS.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.author },
    reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
    reviewBody: r.body,
  })),
});

export const articleLd = ({ title, description, image, author, authorUrl, datePublished, url }) => ({
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  headline: title,
  description,
  image,
  author: {
    "@type": "Person",
    name: author,
    ...(authorUrl ? { url: authorUrl } : {}),
  },
  publisher: {
    "@type": "Organization",
    name: "Physio Hyve Clinic",
    logo: {
      "@type": "ImageObject",
      url: typeof window !== "undefined" ? `${window.location.origin}/logo-icon-256.png` : "/logo-icon-256.png",
    },
  },
  datePublished,
  mainEntityOfPage: url,
  // Speakable schema → Google Assistant / voice search featured snippets
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", ".prose-hyve p:first-of-type", ".prose-hyve h2"],
    xpath: ["/html/head/title"],
  },
});

export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
